import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon } from '../ui/Icon';

export interface NavLink {
  id: string;
  label: string;
  href: string;
}

const NAV_LINKS: NavLink[] = [
  { id: 'home', label: 'Home', href: '#home' },
  { id: 'speisekarte', label: 'Speisekarte', href: '#speisekarte' },
  { id: 'weine', label: 'Weine', href: '#weine' },
  { id: 'reservierung', label: 'Reservierung', href: '#reservierung' },
  { id: 'bewertungen', label: 'Bewertungen', href: '#bewertungen' },
  { id: 'oeffnungszeiten', label: 'Öffnungszeiten', href: '#oeffnungszeiten' },
  { id: 'social', label: 'Social Media', href: '#social' },
  { id: 'standort', label: 'Standort', href: '#standort' },
  { id: 'galerie', label: 'Galerie', href: '#galerie' },
  { id: 'impressum', label: 'Impressum', href: '#impressum' },
];

export interface NavigationProps {
  activeSection?: string;
  onNavigate?: (sectionId: string) => void;
}

const GOLD = '#c9a84c';
const BG = '#1a1a1a';

/**
 * Navigation component with desktop and mobile support.
 * Mobile menu is injected directly into document.body via imperative DOM
 * to avoid any stacking context issues from parent elements (backdrop-blur etc).
 */
export const Navigation: React.FC<NavigationProps> = ({
  activeSection,
  onNavigate,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [internalActiveSection, setInternalActiveSection] = useState<string>('home');
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const menuContainerRef = useRef<HTMLDivElement | null>(null);

  const currentActive = activeSection ?? internalActiveSection;

  // Track active section via IntersectionObserver
  useEffect(() => {
    if (activeSection !== undefined) return;
    const sectionIds = NAV_LINKS.map((l) => l.id);
    const observers: IntersectionObserver[] = [];
    const handleIntersect = (entries: IntersectionObserverEntry[]) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) setInternalActiveSection(entry.target.id);
      });
    };
    sectionIds.forEach((id) => {
      const el = document.getElementById(id);
      if (!el) return;
      const obs = new IntersectionObserver(handleIntersect, {
        rootMargin: '-40% 0px -55% 0px',
        threshold: 0,
      });
      obs.observe(el);
      observers.push(obs);
    });
    return () => observers.forEach((o) => o.disconnect());
  }, [activeSection]);

  // ESC key closes menu
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isMenuOpen) {
        setIsMenuOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isMenuOpen]);

  // Lock body scroll when open
  useEffect(() => {
    document.body.style.overflow = isMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMenuOpen]);

  // ── Imperative mobile menu injected directly into <body> ──────────────────
  // This bypasses ALL stacking contexts from parent elements (backdrop-blur,
  // transform, will-change, etc.) that would otherwise clip position:fixed.
  useEffect(() => {
    // Create container once
    if (!menuContainerRef.current) {
      const div = document.createElement('div');
      div.id = 'fey-mobile-menu-root';
      div.style.cssText = [
        'position:fixed',
        'top:0',
        'left:0',
        'width:100vw',
        'height:100vh',
        'z-index:99999',
        'pointer-events:none',
        'display:block',
      ].join(';');
      document.body.appendChild(div);
      menuContainerRef.current = div;
    }

    const container = menuContainerRef.current;

    const close = () => setIsMenuOpen(false);

    const scrollTo = (id: string) => {
      close();
      const target = document.getElementById(id);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onNavigate?.(id);
    };

    if (isMenuOpen) {
      container.style.pointerEvents = 'auto';
      container.innerHTML = `
        <div id="fey-menu-overlay" style="
          position:absolute;
          inset:0;
          background:${BG};
          opacity:1;
        "></div>
        <div id="fey-menu-panel" style="
          position:absolute;
          inset:0;
          background:${BG};
          display:flex;
          flex-direction:column;
          padding:5rem 1.5rem 2rem;
          overflow-y:auto;
        ">
          <button id="fey-menu-close" aria-label="Menü schließen" style="
            position:absolute;
            top:1rem;
            right:1rem;
            width:2.5rem;
            height:2.5rem;
            display:flex;
            align-items:center;
            justify-content:center;
            background:transparent;
            border:none;
            cursor:pointer;
            color:#d1d5db;
            font-size:1.5rem;
            line-height:1;
          ">✕</button>
          <ul style="list-style:none;margin:0;padding:0;display:flex;flex-direction:column;gap:0.25rem;">
            ${NAV_LINKS.map((link) => {
              const isActive = currentActive === link.id;
              return `
                <li>
                  <a
                    href="${link.href}"
                    data-section="${link.id}"
                    style="
                      display:flex;
                      align-items:center;
                      padding:0.75rem 1rem;
                      border-radius:0.5rem;
                      font-size:1rem;
                      font-weight:500;
                      font-family:system-ui,sans-serif;
                      text-decoration:none;
                      color:${isActive ? GOLD : '#d1d5db'};
                      background:${isActive ? 'rgba(255,255,255,0.05)' : 'transparent'};
                    "
                  >
                    ${isActive ? `<span style="width:4px;height:1.25rem;border-radius:9999px;background:${GOLD};margin-right:0.75rem;flex-shrink:0;display:inline-block;"></span>` : ''}
                    ${link.label}
                  </a>
                </li>
              `;
            }).join('')}
          </ul>
        </div>
      `;

      // Attach click handlers
      container.querySelector('#fey-menu-close')?.addEventListener('click', () => {
        close();
        hamburgerRef.current?.focus();
      });
      container.querySelector('#fey-menu-overlay')?.addEventListener('click', close);
      container.querySelectorAll('a[data-section]').forEach((el) => {
        el.addEventListener('click', (e) => {
          e.preventDefault();
          const id = (el as HTMLElement).dataset.section!;
          scrollTo(id);
        });
      });

      // Focus first link
      const firstLink = container.querySelector<HTMLAnchorElement>('a[data-section]');
      firstLink?.focus();
    } else {
      container.style.pointerEvents = 'none';
      container.innerHTML = '';
    }
  }, [isMenuOpen, currentActive, onNavigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (menuContainerRef.current) {
        document.body.removeChild(menuContainerRef.current);
        menuContainerRef.current = null;
      }
    };
  }, []);

  const handleLinkClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, sectionId: string) => {
      e.preventDefault();
      setIsMenuOpen(false);
      const target = document.getElementById(sectionId);
      if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      onNavigate?.(sectionId);
    },
    [onNavigate]
  );

  const handleLinkKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLAnchorElement>, sectionId: string) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        setIsMenuOpen(false);
        const target = document.getElementById(sectionId);
        if (target) target.scrollIntoView({ behavior: 'smooth', block: 'start' });
        onNavigate?.(sectionId);
      }
    },
    [onNavigate]
  );

  return (
    <nav aria-label="Hauptnavigation">
      {/* Desktop navigation */}
      <ul className="hidden md:flex items-center gap-1 lg:gap-2" role="list">
        {NAV_LINKS.map((link) => {
          const isActive = currentActive === link.id;
          return (
            <li key={link.id}>
              <a
                href={link.href}
                onClick={(e) => handleLinkClick(e, link.id)}
                onKeyDown={(e) => handleLinkKeyDown(e, link.id)}
                aria-current={isActive ? 'page' : undefined}
                className={[
                  'relative px-3 py-2 text-sm font-medium rounded transition-colors duration-200',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2',
                  'focus-visible:ring-[#c9a84c] focus-visible:ring-offset-[#1a1a1a]',
                  isActive ? 'text-[#c9a84c]' : 'text-gray-300 hover:text-[#c9a84c]',
                ].join(' ')}
                style={isActive ? { color: GOLD } : undefined}
              >
                {link.label}
                {isActive && (
                  <span
                    className="absolute bottom-0 left-3 right-3 h-0.5 rounded-full"
                    style={{ backgroundColor: GOLD }}
                    aria-hidden="true"
                  />
                )}
              </a>
            </li>
          );
        })}
      </ul>

      {/* Mobile hamburger button */}
      <button
        ref={hamburgerRef}
        type="button"
        className={[
          'md:hidden flex items-center justify-center w-10 h-10 rounded-lg',
          'text-gray-300 hover:text-[#c9a84c] transition-colors duration-200',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-[#c9a84c]',
          'focus-visible:ring-offset-2 focus-visible:ring-offset-[#1a1a1a]',
        ].join(' ')}
        aria-label={isMenuOpen ? 'Menü schließen' : 'Menü öffnen'}
        aria-expanded={isMenuOpen}
        aria-controls="fey-mobile-menu-root"
        onClick={() => setIsMenuOpen((prev) => !prev)}
      >
        {isMenuOpen ? (
          <Icon name="close" size={22} decorative />
        ) : (
          <Icon name="menu" size={22} decorative />
        )}
      </button>
    </nav>
  );
};
